import React, { useEffect, useState } from 'react';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://api.oneclickservice.in';

const ReviewModal = ({ show, onClose, notificationData, onSubmitted }) => {
  const [step, setStep] = useState(1); // 1..4
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [pricingFair, setPricingFair] = useState(null);
  const [recommend, setRecommend] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setStep(1);
      setRating(0);
      setHoverRating(0);
      setReviewText('');
      setServiceType('');
      setPricingFair(null);
      setRecommend(null);
      setSubmitting(false);
      setError(null);
    }
  }, [show]);

  // ESC to close (when not submitting)
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, submitting, onClose]);

  if (!show) return null;

  const getToken = () =>
    localStorage.getItem('user_token') || localStorage.getItem('auth_token');

  // 1) Submit feedback — hits FIRST
  const submitFeedback = async (feedbackData) => {
    const token = getToken();
    if (!token) {
      setError('Please login to submit feedback.');
      return false;
    }

    const res = await fetch(`${API_BASE}/api/web/submit-feedback`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(feedbackData),
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON body */
    }

    // Surface backend message verbatim (e.g. "Feedback already submitted")
    if (!res.ok || !data?.success) {
      setError(
        data?.message ||
          data?.error ||
          `Failed to submit feedback (HTTP ${res.status}).`
      );
      return false;
    }
    return true;
  };

  // 2) Mark as read — hits SECOND, only after successful submit
  const markNotificationAsRead = async (notificationId) => {
    const token = getToken();
    if (!token || !notificationId) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/web/user/feedback-notifications/${notificationId}/read`,
        {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!res.ok) {
        // Non-fatal — feedback already saved. Log only.
        console.warn('Mark-as-read failed:', res.status);
      }
    } catch (e) {
      console.warn('Mark-as-read network error:', e);
    }
  };

  const submitReview = async () => {
    setError(null);

    // Inline validation → error banner, not alert()
    if (rating === 0) return setError('Please select a rating.');
    if (!reviewText.trim()) return setError('Please describe your experience.');
    if (!serviceType) return setError('Please select a service type.');
    if (pricingFair === null) return setError('Please let us know if pricing was fair.');
    if (recommend === null) return setError('Please let us know if you would recommend.');

    setSubmitting(true);
    try {
      const notification = notificationData?.notification || {};
      const metadata = notification?.metadata || {};

      const payload = {
        technicianId:
          metadata?.technicianId || notificationData?.technicianId || '',
        viewLogId: metadata?.viewId || notificationData?.viewLogId || '',
        workQualityRating: rating,
        behaviorRating: rating,
        personalityRating: rating,
        experience: reviewText.trim(),
        serviceType,
        pricingFair: pricingFair === 'fair',
        wouldRecommend: recommend === 'yes',
      };

      const ok = await submitFeedback(payload);
      if (!ok) return; // error already set with backend message

      // Only now mark as read
      const notificationId =
        notificationData?.notificationId || notification?._id;
      await markNotificationAsRead(notificationId);

      onSubmitted?.(); // let parent refetch / update count
      onClose();
    } catch (err) {
      console.error('submitReview error:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () =>
    [1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        role="radio"
        aria-checked={rating === i}
        aria-label={`${i} star${i > 1 ? 's' : ''}`}
        onClick={() => setRating(i)}
        onMouseEnter={() => setHoverRating(i)}
        onMouseLeave={() => setHoverRating(0)}
        className={i <= (hoverRating || rating) ? 'active' : ''}
        style={{
          cursor: 'pointer',
          fontSize: '32px',
          background: 'none',
          border: 'none',
          padding: 0,
          color: i <= (hoverRating || rating) ? '#ffb400' : '#ccc',
          transition: 'all 0.2s',
        }}
      >
        ★
      </button>
    ));

  const technicianName =
    notificationData?.technicianName ||
    notificationData?.notification?.metadata?.technicianName ||
    'Technician';

  const canProceedStep1 = rating > 0;
  const canProceedStep2 = reviewText.trim().length > 0;
  const canProceedStep3 = Boolean(serviceType) && pricingFair !== null;
  const canSubmit = recommend !== null && !submitting;

  return (
    <div
      className={`review-modal ${show ? 'show' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-modal-title"
    >
      <div className="modal-content" style={{ position: 'relative' }}>
        {/* Close button — ALWAYS visible, disabled while submitting */}
        <button
          type="button"
          className="close-modal"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close review modal"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'transparent',
            border: 'none',
            fontSize: 24,
            lineHeight: 1,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.4 : 1,
            color: '#333',
            zIndex: 2,
          }}
        >
          ×
        </button>

        {/* Error banner — shows backend message */}
        {error && (
          <div
            role="alert"
            style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '10px 40px 10px 10px',
              borderRadius: '8px',
              marginBottom: '15px',
              fontSize: '14px',
              position: 'relative',
            }}
          >
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              style={{
                position: 'absolute',
                top: 6,
                right: 8,
                background: 'transparent',
                border: 'none',
                color: '#721c24',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* STEP 1 — Rating */}
        {step === 1 && (
          <div className="step active" id="m-step1">
            <h3
              id="review-modal-title"
              style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, paddingRight: 24 }}
            >
              Rate your experience with {technicianName}
            </h3>
            <div
              className="stars"
              role="radiogroup"
              aria-label="Star rating"
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                margin: '15px 0',
              }}
            >
              {renderStars()}
            </div>
            <p style={{ textAlign: 'center', color: '#666', fontSize: 14 }}>
              {rating > 0
                ? `Selected: ${rating} star${rating > 1 ? 's' : ''}`
                : 'Tap a star to rate'}
            </p>
            <button
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              style={primaryBtn(canProceedStep1)}
            >
              Next
            </button>
          </div>
        )}

        {/* STEP 2 — Review text */}
        {step === 2 && (
          <div className="step active" id="m-step2">
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
              Describe your experience
            </p>
            <textarea
              placeholder="Tell us about your experience with this technician..."
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                minHeight: 100,
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 15 }}>
              <button onClick={() => setStep(1)} style={secondaryBtn()}>
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                style={primaryBtn(canProceedStep2)}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Service & pricing */}
        {step === 3 && (
          <div className="step active" id="m-step3">
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
              Service Details
            </p>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                marginBottom: 15,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <option value="">Select Service Type</option>
              {[
                'Plumbing',
                'Electrician',
                'Carpenter',
                'Painter',
                'Cleaner',
                'Delivery',
                'IT Support',
                'Other',
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
              Was the pricing fair?
            </p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
              <button
                type="button"
                onClick={() => setPricingFair('fair')}
                aria-pressed={pricingFair === 'fair'}
                style={choiceBtn(pricingFair === 'fair', '#000')}
              >
                Yes, Fair
              </button>
              <button
                type="button"
                onClick={() => setPricingFair('unfair')}
                aria-pressed={pricingFair === 'unfair'}
                style={choiceBtn(pricingFair === 'unfair', '#dc3545')}
              >
                No, Unfair
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(2)} style={secondaryBtn()}>
                Back
              </button>
              <button
                onClick={() => setStep(4)}
                disabled={!canProceedStep3}
                style={primaryBtn(canProceedStep3)}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — Recommend + Submit */}
        {step === 4 && (
          <div className="step active" id="m-step4">
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
              Would you recommend this technician?
            </p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <button
                type="button"
                onClick={() => setRecommend('yes')}
                aria-pressed={recommend === 'yes'}
                style={choiceBtn(recommend === 'yes', '#28a745')}
              >
                👍 Yes
              </button>
              <button
                type="button"
                onClick={() => setRecommend('no')}
                aria-pressed={recommend === 'no'}
                style={choiceBtn(recommend === 'no', '#dc3545')}
              >
                👎 No
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(3)} style={secondaryBtn()}>
                Back
              </button>
              <button
                className="submit-btn"
                onClick={submitReview}
                disabled={!canSubmit}
                style={{
                  flex: 2,
                  padding: 12,
                  border: 'none',
                  background: canSubmit
                    ? 'linear-gradient(135deg, #ff2e2e, #cc0000)'
                    : '#ccc',
                  color: '#fff',
                  borderRadius: 8,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  fontSize: 16,
                  transition: 'all 0.3s',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>

            {recommend === null && (
              <p
                style={{
                  textAlign: 'center',
                  color: '#999',
                  fontSize: 13,
                  marginTop: 10,
                }}
              >
                Please select Yes or No to continue
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Small style helpers to keep JSX readable
const primaryBtn = (enabled) => ({
  width: '100%',
  padding: 12,
  border: 'none',
  background: enabled ? '#000' : '#ccc',
  color: '#fff',
  borderRadius: 8,
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontWeight: 600,
  marginTop: 15,
});

const secondaryBtn = () => ({
  flex: 1,
  padding: 12,
  border: '1px solid #ddd',
  background: '#fff',
  color: '#333',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 600,
});

const choiceBtn = (active, activeColor) => ({
  flex: 1,
  padding: 10,
  border: '2px solid #ddd',
  borderRadius: 8,
  background: active ? activeColor : '#fff',
  color: active ? '#fff' : '#333',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'all 0.2s',
});

export default ReviewModal;